import { Plus, Search, Users } from 'lucide-react';
import { useState } from 'react';

import { useUsuario } from '@/lib/auth/contexto';
import { podeNaPlataforma } from '@/lib/auth/permissoes';
import { mensagemDeErro } from '@/lib/erros';
import { juntarPaginas } from '@/lib/paginacao';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { TabelaDados } from '@/ui/components/tabela-dados';
import { CabecalhoPagina } from '@/ui/patterns/cabecalho-pagina';
import { PaginaLista } from '@/ui/patterns/pagina-lista';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Input } from '@/ui/primitives/input';
import { type UsuarioResumo } from '@fiscoai/contracts';

import { AcoesPessoa } from './acoes-pessoa';
import { useAlterarUsuarios, usePerfis, useUsuarios } from './api';
import { colunasUsuarios } from './colunas-usuarios';
import { DialogoNovoUsuario } from './dialogo-novo-usuario';
import { DialogoRedefinirSenha } from './dialogo-redefinir-senha';
import { SeletorPerfilPlataforma, TextoPerfilPlataforma } from './perfil-plataforma';

/** Quem só vê (`usuarios.ler`) não cadastra, não desativa nem troca perfil: isso é do admin. */
export function PaginaUsuarios() {
  const eu = useUsuario();
  const gerencia = podeNaPlataforma(eu, 'plataforma.usuarios.gerenciar');
  const [busca, setBusca] = useState('');
  const [novo, setNovo] = useState(false);
  const [senhaDe, setSenhaDe] = useState<UsuarioResumo | null>(null);
  const consulta = useUsuarios(busca.trim());
  const perfis = usePerfis();
  const { atualizar } = useAlterarUsuarios();

  const trocarPerfil = (u: UsuarioResumo, chave: string | null) => {
    atualizar.mutate(
      { id: u.id, dados: { chavePerfilPlataforma: chave } },
      {
        onSuccess: (salvo) => {
          toast.success(`${u.nome} agora é ${salvo.perfilPlataforma?.nome ?? 'só por empresa'}`);
        },
        onError: (erro) => toast.error(mensagemDeErro(erro)),
      },
    );
  };
  // A própria linha fica em texto: o server não deixa ninguém mudar o próprio perfil.
  const celulaPerfil = (u: UsuarioResumo) =>
    gerencia && perfis.data && u.id !== eu.id ? (
      <SeletorPerfilPlataforma usuario={u} perfis={perfis.data} aoMudar={trocarPerfil} />
    ) : (
      <TextoPerfilPlataforma usuario={u} />
    );

  const alternar = (u: UsuarioResumo) => {
    atualizar.mutate(
      { id: u.id, dados: { ativo: !u.ativo } },
      {
        onSuccess: () => toast.success(u.ativo ? `${u.nome} desativado` : `${u.nome} reativado`),
        onError: (erro) => toast.error(mensagemDeErro(erro)),
      },
    );
  };

  return (
    <PaginaLista
      cabecalho={
        <CabecalhoPagina
          titulo="Pessoas"
          descricao="Quem entra na instalação. O que cada um vê depende dos vínculos com as empresas."
          acoes={
            gerencia && (
              <Button
                variante="primario"
                onClick={() => {
                  setNovo(true);
                }}
              >
                <Plus aria-hidden />
                Nova pessoa
              </Button>
            )
          }
        />
      }
      filtros={
        <label className="relative max-w-md">
          <span className="sr-only">Buscar pessoas</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-texto-fraco"
            aria-hidden
          />
          <Input
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
            }}
            placeholder="Nome ou e-mail"
            className="pl-9"
          />
        </label>
      }
      lista={
        <TabelaDados
          rotulo="Pessoas"
          className="flex-1"
          colunas={colunasUsuarios(celulaPerfil)}
          dados={juntarPaginas(consulta.data)}
          idLinha={(u) => u.id}
          carregando={consulta.isPending}
          {...(gerencia
            ? {
                acoesLinha: (u: UsuarioResumo) => (
                  <AcoesPessoa
                    usuario={u}
                    propria={u.id === eu.id}
                    aoAlternar={alternar}
                    aoRedefinirSenha={setSenhaDe}
                  />
                ),
              }
            : {})}
          vazio={
            <EstadoVazio
              icone={<Users />}
              titulo="Ninguém encontrado"
              texto="Confira a busca ou cadastre uma nova pessoa."
            />
          }
        />
      }
      detalhe={
        gerencia && (
          <>
            <DialogoNovoUsuario aberto={novo} aoMudar={setNovo} />
            <DialogoRedefinirSenha
              usuario={senhaDe}
              aoFechar={() => {
                setSenhaDe(null);
              }}
            />
          </>
        )
      }
    />
  );
}
